import { Component } from '@theme/component';
import { CartAddEvent, CartUpdateEvent, ThemeEvents } from '@theme/events';

/**
 * @typedef {Object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The hidden input containing the variant ID
 * @property {HTMLInputElement} [quantityInput] - The quantity input (from quantity selector)
 * @property {HTMLElement} [addToCartButtonContainer] - The add to cart button container
 * @property {HTMLButtonElement} [addToCartButton] - The add to cart button
 * @property {HTMLElement} [buttonText] - The button text element
 * @property {HTMLElement} [liveRegion] - The live region for accessibility announcements
 * @property {HTMLElement} [quantityLabelCartCount] - The cart count label
 */

/**
 * Product form component that handles add to cart / update cart functionality.
 * Automatically switches between add and update based on whether the item is in cart.
 *
 * @extends {Component<ProductFormRefs>}
 */
export class ProductFormComponent extends Component {
  /**
   * Current cart quantity for the selected variant
   * @type {number}
   */
  #cartQuantity = 0;

  /**
   * Whether the form is currently submitting
   * @type {boolean}
   */
  #isSubmitting = false;

  connectedCallback() {
    super.connectedCallback();

    // Initialize cart quantity from data attribute
    const addToCartContainer = this.refs.addToCartButtonContainer;
    if (addToCartContainer) {
      this.#cartQuantity = parseInt(addToCartContainer.dataset.cartQuantity || '0', 10);
    }

    // Listen for cart updates
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);

    // Listen for variant updates to sync the hidden variant ID input
    const closestSection = this.closest('.shopify-section, dialog');
    if (closestSection) {
      closestSection.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);

    const closestSection = this.closest('.shopify-section, dialog');
    if (closestSection) {
      closestSection.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    }
  }

  /**
   * Handles variant update events to sync the hidden variant ID input
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    const variant = event.detail?.resource;
    const data = event.detail?.data;

    // If a new product was loaded (combined listing), check if this form matches
    if (data?.newProduct) {
      // Update our product ID to match
      if (data.newProduct.id) {
        this.dataset.productId = data.newProduct.id;
      }
    } else if (data?.productId && this.dataset.productId && data.productId !== this.dataset.productId) {
      // Not our product, skip
      return;
    }

    if (!variant) return;

    // Update the hidden variant ID input
    const variantId = variant.id?.toString();
    if (variantId) {
      const variantInput = this.refs.variantId || this.querySelector('input[name="id"]');
      if (variantInput instanceof HTMLInputElement) {
        variantInput.value = variantId;
        variantInput.setAttribute('value', variantId);
      }

      // Update quantity selector's variant reference
      const quantitySelector = this.querySelector('quantity-selector-component');
      if (quantitySelector instanceof HTMLElement) {
        quantitySelector.dataset.variantId = variantId;
      }

      // Re-check cart quantity for the new variant
      if (window.Shopify?.cart) {
        const cartQty = this.#getCartQuantityForVariant(window.Shopify.cart, variantId);
        this.#updateCartQuantity(cartQty);
      }
    }
  };

  /**
   * Handles cart update events to sync cart quantity
   * @param {CustomEvent} event - The cart update event
   */
  #handleCartUpdate = (event) => {
    // Skip if this component triggered the event
    if (event.target === this) return;

    const cart = event.detail?.resource;
    if (!cart?.items) return;

    const variantId = this.#getVariantId();
    if (!variantId) return;

    const cartQty = this.#getCartQuantityForVariant(cart, variantId);

    this.#updateCartQuantity(cartQty);
  };

  /**
   * Gets the current variant ID
   * @returns {string | null}
   */
  #getVariantId() {
    const variantInput = this.refs.variantId || this.querySelector('input[name="id"]');
    return variantInput instanceof HTMLInputElement ? variantInput.value : null;
  }

  /**
   * Gets the current quantity from the quantity selector
   * @returns {number}
   */
  #getQuantity() {
    // Try to find quantity input via refs first
    let quantityInput = this.refs.quantityInput;
    
    // If not in refs, search for it
    if (!quantityInput) {
      const quantitySelector = this.querySelector('quantity-selector-component');
      quantityInput = quantitySelector?.querySelector('input[name="quantity"]');
    }

    if (quantityInput instanceof HTMLInputElement) {
      return parseInt(quantityInput.value, 10) || 1;
    }

    // Default to the quantity default from data attribute
    return parseInt(this.dataset.quantityDefault || '1', 10);
  }

  /**
   * Calculates total cart quantity for a variant across all line items.
   * @param {any} cart - Cart data
   * @param {string} variantId - Variant ID
   * @returns {number}
   */
  #getCartQuantityForVariant(cart, variantId) {
    if (!cart?.items) return 0;
    return cart.items.reduce((total, item) => {
      if (item?.variant_id?.toString() === variantId) {
        return total + (item.quantity || 0);
      }
      return total;
    }, 0);
  }

  /**
   * Updates the cart quantity and UI state
   * @param {number} quantity - The new cart quantity
   */
  #updateCartQuantity(quantity) {
    this.#cartQuantity = quantity;

    // Update the add to cart container data attribute
    const addToCartContainer = this.refs.addToCartButtonContainer;
    if (addToCartContainer) {
      addToCartContainer.dataset.cartQuantity = quantity.toString();
    }

    // Update button text
    this.#updateButtonText();

    // Update quantity label if present
    const quantityLabelCartCount = this.refs.quantityLabelCartCount;
    if (quantityLabelCartCount) {
      quantityLabelCartCount.textContent = `(${quantity} ${Theme.translations?.in_cart || 'in cart'})`;
      quantityLabelCartCount.classList.toggle('hidden', quantity === 0);
    }

    // Update quantity selector value to match cart
    const quantitySelector = this.querySelector('quantity-selector-component');
    if (quantitySelector) {
      const quantityInput = quantitySelector.querySelector('input[name="quantity"]');
      if (quantityInput instanceof HTMLInputElement) {
        quantityInput.value = quantity.toString();
        quantityInput.setAttribute('data-cart-quantity', quantity.toString());
        // Trigger button state update
        const component = /** @type {any} */ (quantitySelector);
        if (typeof component.updateButtonStates === 'function') {
          component.updateButtonStates();
        }
      }
    }
  }

  /**
   * Updates the button text based on cart state
   */
  #updateButtonText() {
    const addToCartContainer = this.refs.addToCartButtonContainer;
    const buttonText = this.refs.buttonText || addToCartContainer?.querySelector('[ref="buttonText"]');
    
    if (buttonText && addToCartContainer) {
      const addText = addToCartContainer.dataset.addText || 'Add to cart';
      const updateText = addToCartContainer.dataset.updateText || 'Update cart';
      
      buttonText.textContent = this.#cartQuantity > 0 ? updateText : addText;
    }
  }

  /**
   * Handles form submission
   * @param {SubmitEvent} event - The submit event
   */
  async handleSubmit(event) {
    event.preventDefault();

    if (this.#isSubmitting) return;

    const variantId = this.#getVariantId();
    if (!variantId) return;

    const desiredQuantity = this.#getQuantity();
    const isInCart = this.#cartQuantity > 0;
    const addonPayload = this.#getAddonPayload();
    const hasAddonPayload = this.#hasAddonPayload(addonPayload);
    const shouldUpdate = isInCart && !hasAddonPayload;

    this.#isSubmitting = true;

    // Disable button during submission
    const addToCartButton = this.refs.addToCartButton || this.querySelector('button[type="submit"]');
    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = true;
    }

    try {
      let cartData;

      if (shouldUpdate) {
        // Update existing cart item
        cartData = await this.#updateCart(variantId, desiredQuantity);
      } else {
        // Add new item to cart
        cartData = await this.#addToCart(variantId, desiredQuantity, addonPayload);
      }

      if (cartData) {
        // Update global cart reference
        if (typeof window !== 'undefined') {
          // @ts-ignore
          window.Shopify = window.Shopify || {};
          // @ts-ignore
          window.Shopify.cart = cartData;
        }

        // Update local cart quantity
        const cartQty = this.#getCartQuantityForVariant(cartData, variantId);
        this.#updateCartQuantity(cartQty);

        // Dispatch cart event
        const itemCount = typeof cartData.item_count === 'number' ? cartData.item_count : undefined;
        const sourceId = this.id || this.dataset.sectionId || 'product-form-component';

        const EventClass = shouldUpdate ? CartUpdateEvent : CartAddEvent;
        this.dispatchEvent(
          new EventClass(cartData, sourceId, {
            itemCount,
            variantId,
            source: 'product-form-component',
            productId: this.dataset.productId,
          })
        );

        // Show success animation
        this.#showSuccessAnimation();

        // Announce to screen readers
        this.#announceToScreenReader(shouldUpdate ? 'Cart updated' : 'Added to cart');
      }
    } catch (error) {
      console.error('Failed to update cart:', error);
      this.#showError(error.message || 'Error updating cart');
    } finally {
      this.#isSubmitting = false;

      // Re-enable button
      if (addToCartButton instanceof HTMLButtonElement) {
        addToCartButton.disabled = false;
      }
    }
  }

  /**
   * Adds item to cart
   * @param {string} variantId - The variant ID
   * @param {number} quantity - The quantity to add
   * @param {{items: Array<any>, properties: Record<string, string>}} addonPayload - Add-on payload
   * @returns {Promise<Object>} The cart data
   */
  async #addToCart(variantId, quantity, addonPayload) {
    const baseProperties = this.#collectFormProperties();
    const payload = addonPayload || { items: [], properties: {} };
    const hasAddons = this.#hasAddonPayload(payload);
    const items = [];

    if (hasAddons) {
      const addonToken = this.#createAddonToken();
      const parentProperties = {
        ...baseProperties,
        ...payload.properties,
        _addon_parent_token: addonToken,
        _addon_parent_role: 'parent',
      };

      items.push({
        id: variantId,
        quantity,
        properties: parentProperties,
      });

      items.push(...this.#buildAddonItems(payload.items, quantity, addonToken));
    } else {
      const parentProperties = Object.keys(baseProperties).length ? baseProperties : undefined;
      items.push({
        id: variantId,
        quantity,
        ...(parentProperties ? { properties: parentProperties } : {}),
      });
    }

    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({
        items,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.description || errorData.message || 'Error adding to cart');
    }

    // cart/add.js returns the added items, not the full cart
    // Fetch the full cart to get accurate state
    const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, {
      headers: { 'Accept': 'application/json' },
    });

    if (cartResponse.ok) {
      return await cartResponse.json();
    }

    return await response.json();
  }

  /**
   * Reads add-on payload from data attributes.
   * @returns {{items: Array<any>, properties: Record<string, string>}}
   */
  #getAddonPayload() {
    const items = this.#parseAddonJson(this.dataset.addonItems, []);
    const properties = this.#parseAddonJson(this.dataset.addonProperties, {});
    return { items, properties };
  }

  /**
   * Checks if add-on payload has data.
   * @param {{items: Array<any>, properties: Record<string, string>}} payload
   * @returns {boolean}
   */
  #hasAddonPayload(payload) {
    if (!payload) return false;
    if (Array.isArray(payload.items) && payload.items.length > 0) return true;
    return payload.properties && Object.keys(payload.properties).length > 0;
  }

  /**
   * Parses JSON safely.
   * @param {string | undefined} value
   * @param {any} fallback
   * @returns {any}
   */
  #parseAddonJson(value, fallback) {
    if (!value) return fallback;
    try {
      return JSON.parse(value);
    } catch (error) {
      return fallback;
    }
  }

  /**
   * Normalizes Shopify GID to numeric ID for Ajax API.
   * @param {string} value
   * @returns {string}
   */
  #normalizeVariantId(value) {
    if (!value) return '';
    const str = String(value);
    if (str.startsWith('gid://')) {
      const parts = str.split('/');
      return parts[parts.length - 1];
    }
    return str;
  }

  /**
   * Builds child add-on line items for cart/add.
   * @param {Array<any>} addonItems
   * @param {number} parentQuantity
   * @param {string} addonToken
   * @returns {Array<any>}
   */
  #buildAddonItems(addonItems, parentQuantity, addonToken) {
    if (!Array.isArray(addonItems)) return [];
    return addonItems
      .map((item) => {
        const variantId = this.#normalizeVariantId(item.variantId || item.id);
        if (!variantId) return null;
        const pricingBehavior = item.pricingBehavior || 'per-item';
        const quantity = pricingBehavior === 'per-order' ? (item.quantity || 1) : parentQuantity;
        return {
          id: variantId,
          quantity,
          properties: {
            ...(item.properties || {}),
            _addon_parent_token: addonToken,
            _addon_parent_role: 'child',
            _addon_pricing_behavior: pricingBehavior,
          },
        };
      })
      .filter(Boolean);
  }

  /**
   * Builds unique token for grouping add-on line items.
   * @returns {string}
   */
  #createAddonToken() {
    return `addon_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  }

  /**
   * Collects line item properties from the product form.
   * @returns {Record<string, string>}
   */
  #collectFormProperties() {
    const properties = {};
    const form = this.querySelector('form');
    if (!form) return properties;

    const formData = new FormData(form);
    for (const [key, value] of formData.entries()) {
      if (!key.startsWith('properties[')) continue;
      const propName = key.slice('properties['.length, -1);
      if (propName && value !== null && value !== '') {
        properties[propName] = String(value);
      }
    }

    return properties;
  }

  /**
   * Updates cart item quantity
   * @param {string} variantId - The variant ID
   * @param {number} quantity - The new quantity
   * @returns {Promise<Object>} The cart data
   */
  async #updateCart(variantId, quantity) {
    const formData = new FormData();
    formData.append(`updates[${variantId}]`, quantity.toString());

    const response = await fetch(Theme.routes.cart_update_url, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.description || errorData.message || 'Error updating cart');
    }

    return await response.json();
  }

  /**
   * Shows success animation on the button
   */
  #showSuccessAnimation() {
    const addToCartContainer = this.refs.addToCartButtonContainer;
    if (!addToCartContainer) return;

    const button = addToCartContainer.querySelector('button');
    if (button) {
      button.classList.add('atc-added');
      setTimeout(() => {
        button.classList.remove('atc-added');
      }, 2000);
    }
  }

  /**
   * Shows error message
   * @param {string} message - The error message
   */
  #showError(message) {
    const errorElement = this.querySelector('[ref="addToCartTextError"]');
    if (errorElement) {
      errorElement.textContent = message;
      errorElement.classList.remove('hidden');
      setTimeout(() => {
        errorElement.classList.add('hidden');
      }, 5000);
    }
  }

  /**
   * Announces message to screen readers
   * @param {string} message - The message to announce
   */
  #announceToScreenReader(message) {
    const liveRegion = this.refs.liveRegion;
    if (liveRegion) {
      liveRegion.textContent = message;
      setTimeout(() => {
        liveRegion.textContent = '';
      }, 1000);
    }
  }
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
